import classnames from 'classnames'
import * as React from 'react'

import {
  Button,
  DialogTrigger,
  Dropdown,
  DropdownItem,
  Spinner,
  Tooltip,
  TooltipTrigger,
  useDialogState,
  useDropdownState,
  useTooltipState,
} from '../../components'
import { IdeasDialog, saveIdea } from '../../modals/ideas'
import ChartLayout from '../../models/chart_layout'
import Quote from '../../models/quote'
import { getIsMobileScreenSize } from '../../utils'

export function IdeasDropdown({
  model,
  rounding,
  theme = 'headerItem',
  dropdownButtonClassName,
}: {
  model: ChartLayout
  rounding?: 'none' | 'regular'
  theme?: string
  dropdownButtonClassName?: string
}) {
  const tooltipState = useTooltipState({ placement: 'right-start' })
  const [isAnyQuoteLoading, setIsAnyQuoteLoading] = React.useState(false)
  const ref = React.useRef(null)
  const dialog = useDialogState()
  const dropdown = useDropdownState()
  const hideTooltip = tooltipState.hide

  React.useEffect(() => {
    const checkIfAnyQuoteIsLoading = () => {
      const fetchingQuotes = Quote.select<Quote>((quote) => quote.isFetching && quote.fetchedAt === -1)
      setIsAnyQuoteLoading(fetchingQuotes.length > 0)
    }

    Quote.bind('change', checkIfAnyQuoteIsLoading)
    return () => {
      Quote.unbind('change', checkIfAnyQuoteIsLoading)
    }
  }, [])

  React.useEffect(() => {
    if (!isAnyQuoteLoading || !dropdown.visible) {
      hideTooltip()
    }
  }, [isAnyQuoteLoading, dropdown.visible, hideTooltip])

  return (
    <>
      <Dropdown
        ref={ref}
        state={dropdown}
        aria-label="Idea"
        rounding={rounding}
        label={
          <Button
            leftContent="idea"
            theme={theme}
            className={dropdownButtonClassName}
            data-testid="chart-toolbar-ideas"
            contentClass="hidden sm:flex"
            label="Ideas"
          >
            Ideas
          </Button>
        }
      >
        <DropdownItem
          as={isAnyQuoteLoading ? TooltipTrigger : undefined}
          state={isAnyQuoteLoading ? tooltipState : undefined}
          leftContent="ideaSave"
          data-testid="charts-layout-save-idea-btn"
          closeOnClick={false}
          className="cursor-pointer"
          onClick={() => {
            if (isAnyQuoteLoading) {
              tooltipState.show()
              return
            }
            saveIdea(model)
          }}
        >
          Save idea
        </DropdownItem>
        <DropdownItem
          as={DialogTrigger}
          state={dialog}
          leftContent="ideaLoad"
          data-testid="charts-layout-load-idea-btn"
        >
          Load idea
        </DropdownItem>
      </Dropdown>
      <IdeasDialog state={dialog} />
      <Tooltip state={tooltipState} className="flex">
        <div className="mt-1">
          <Spinner width={12} />
        </div>
        <span className={classnames('ml-2', { 'w-40': getIsMobileScreenSize() })}>
          Some data is still loading. Please wait.
        </span>
      </Tooltip>
    </>
  )
}
